import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentPersonalInfoData,
  type EnrollmentPersonalInfoSubmitInput,
  type EnrollmentPersonalInfoSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment PersonalInfo data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentPersonalInfoData>
 * @example const response = await getEnrollmentPersonalInfoData()
 */
export const getEnrollmentPersonalInfoData = async (context?: DefaultContext): Promise<EnrollmentPersonalInfoData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentPersonalInfoData',
      query: `mutation getEnrollmentPersonalInfoData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              userInfo {
                employmentStatus
                netWorthRange
                occupation
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentPersonalInfoData }
  return response.submitEnrollment
}

export const useGetEnrollmentPersonalInfoData = createQueryHook<undefined, EnrollmentPersonalInfoData>(
  getEnrollmentPersonalInfoData,
)

/**
 * Submit Enrollment ConfirmPersonalInfo data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentConfirmPersonalInfoSubmitOutput>
 * @example const response = await submitEnrollmentConfirmPersonalInfoData();
 */
export const submitEnrollmentPersonalInfoData = async (
  enrollmentPersonalInfoSubmitInput: EnrollmentPersonalInfoSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentPersonalInfoSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentPersonalInfoData',
      query: `mutation submitEnrollmentPersonalInfoData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentPersonalInfoSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentPersonalInfoSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentPersonalInfoData = createMutationHook<
  EnrollmentPersonalInfoSubmitInput,
  EnrollmentPersonalInfoSubmitOutput
>(submitEnrollmentPersonalInfoData)
